/**
 * HTTP 基础层
 * ---------------------------------------------------------------------------
 * 全项目只有这一处直接调用 fetch。所有业务请求都要经过 `request()`，
 * 好处是鉴权、错误格式、返回体解包这三件事只需要维护一份。
 *
 * 后端约定（见 server/src/main.ts 与 common/response.interceptor.ts）：
 *   - 全局前缀 /api/v1
 *   - 成功：{ "data": <真正的业务数据>, "meta": { "requestId": "..." } }
 *   - 失败：{ "error": { "code": "...", "message": string | string[], "requestId": "..." } }
 *   - 鉴权：Authorization: Bearer <accessToken>
 */

import { clearSession, readAccessToken } from '@/lib/auth/session-storage';

/**
 * 后端地址。三个来源，**桌面壳的优先级最高**：
 *
 *   1. `window.ToniServer.baseUrl` —— 桌面客户端注入。门店的服务器地址各不相同，
 *      装在收银机上的客户端**必须能改**，写死在打包产物里等于每家店都要单独打包。
 *   2. `VITE_API_BASE_URL` —— 构建期注入，网页版部署用。
 *   3. localhost —— 只有本机开发能用。
 *
 * 用函数而不是常量：桌面壳里改完服务器地址不重启就能生效。
 */
declare global {
  interface Window {
    ToniServer?: { baseUrl?: string };
  }
}

export function apiBaseUrl(): string {
  const injected =
    typeof window !== 'undefined' ? window.ToniServer?.baseUrl : undefined;
  if (injected) return injected.replace(/\/+$/, '');
  const configured = import.meta.env.VITE_API_BASE_URL;
  if (!configured) return 'http://localhost:3200/api/v1';
  /*
    **允许配成相对路径**（`/api/v1`）。前后端同域部署时这是最省事的写法：
    不用把域名写死进构建产物，也没有跨域问题。
    但 `new URL()` 只认绝对地址，所以这里补上当前站点的 origin——
    不补的话每个请求都会在 `new URL()` 那一行抛异常，
    而异常信息是"Invalid URL"，完全看不出是配置的问题。
  */
  if (configured.startsWith('/')) {
    return `${window.location.origin}${configured.replace(/\/+$/, '')}`;
  }
  return configured.replace(/\/+$/, '');
}



/**
 * 请求**最终**打到哪台后端。
 *
 * 和 `apiBaseUrl()` 的区别只在连正式服调试时：那时走的是 Vite dev proxy
 * （见 vite.config.ts），`apiBaseUrl()` 是同源的 http://localhost:5200/api/v1，
 * 看起来完全本地，数据却落在生产库里。判断"连没连生产"必须用这个函数——
 * 用 `apiBaseUrl()` 会一律判成本地，红色告警在最需要它的时候静默失效。
 *
 * 代理只在 dev server 里生效，构建产物里 VITE_DEV_API_PROXY 为空，
 * 于是自动退回 `apiBaseUrl()`，桌面端和网页版都不受影响。
 */
export function apiUpstreamUrl(): string {
  const proxyTarget = import.meta.env.VITE_DEV_API_PROXY?.trim();
  return proxyTarget ? proxyTarget.replace(/\/+$/, '') : apiBaseUrl();
}

/**
 * 现在连的是不是生产环境。
 *
 * 判断依据是**域名不是 localhost**——界面跑在本地、数据落在生产库这种组合
 * 最容易出事：随手建的测试会员、乱点的退款，都是真的。
 * 顶栏据此显示红色标记。
 */
export function isProductionApi(): boolean {
  if (typeof window === 'undefined') return false;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  const apiIsLocal = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])/.test(apiUpstreamUrl());
  /*
    **只有"界面在本地、数据在线上"这一种组合才警告。**
    同域部署时页面本身就在生产域名下，那是正常使用，
    再挂个红色「连生产」只会变成天天看见的噪音，真出事时也就没人看了。
  */
  return local && !apiIsLocal;
}

/** 后端成功返回体 */
type SuccessEnvelope<T> = { data: T; meta: { requestId: string } };

/** 后端失败返回体 */
type ErrorEnvelope = {
  error: { code: string; message: string | string[]; requestId?: string };
};

/**
 * 统一的接口异常。
 * 页面里 catch 到它就能直接拿 message 弹提示，拿 code 做分支判断，
 * 拿 requestId 让门店把这串 ID 报给客服排查。
 */
export class ApiError extends Error {
  /** 后端业务错误码，如 AUTH_TOKEN_REQUIRED、ORDER_VERSION_CONFLICT */
  readonly code: string;
  /** HTTP 状态码；0 表示请求根本没发出去（断网、后端没启动） */
  readonly status: number;
  /** 后端为这次请求生成的追踪 ID，报障时让门店把它念给客服 */
  readonly requestId?: string;

  constructor(
    code: string,
    /** 已经拼成一句话的错误信息，可直接展示给用户 */
    message: string,
    status: number,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }

  /** 登录态失效：token 过期、被踢下线或压根没登录 */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** 权限不足：登录了但这个账号不能做这件事 */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** 网络层失败（断网、后端未启动、跨域被拦），和业务错误要分开提示 */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

/** 登录态失效时的回调，由 AuthProvider 注册，用来把用户送回登录页 */
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** 查询参数：值为 undefined / null / '' 的键会被丢掉，避免触发后端的参数白名单校验 */
export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

function buildUrl(path: string, query?: QueryParams): string {
  const url = new URL(
    `${apiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`,
  );
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/** 后端校验失败时 message 是字符串数组，这里拼成一句话 */
function normalizeMessage(message: string | string[]): string {
  return Array.isArray(message) ? message.join('；') : message;
}

export type RequestOptions = {
  query?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * 幂等键，写操作（下单、收款、充值、退款）带上。
   * 服务端已经消费这个请求头：同一 key 和同一请求只产生一笔业务结果，换内容会
   * 返回 409。但按钮仍必须做提交中禁用——幂等防止第二笔交易，不负责消除两次
   * 点击造成的重复加载态、错误提示或无意义网络请求。
   */
  idempotencyKey?: string;
  /** 跳过自动附带的 Authorization 头，目前只有登录接口用得到 */
  skipAuth?: boolean;
};

/**
 * 发起一次接口请求，返回已经解包过的业务数据（即 envelope 里的 data）。
 * 任何非 2xx 都会抛出 ApiError，调用方不需要再判断 response.ok。
 */
export async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!options.skipAuth) {
    const token = readAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (options.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    // fetch 只在网络层失败时 reject（断网、后端没起、被跨域拦截）
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(
      'NETWORK_ERROR',
      '连不上服务器，请检查网络后重试',
      0,
    );
  }

  // 204 之类没有响应体的情况
  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const errorBody = payload as ErrorEnvelope | null;
    const detail = errorBody?.error;
    const apiError = new ApiError(
      detail?.code ?? `HTTP_${response.status}`,
      detail ? normalizeMessage(detail.message) : '服务器开小差了，请稍后重试',
      response.status,
      detail?.requestId,
    );
    // 登录态失效统一在这里处理，业务代码不用每个页面都判断一次
    if (apiError.isUnauthorized) {
      clearSession();
      unauthorizedHandler?.();
    }
    throw apiError;
  }

  return (payload as SuccessEnvelope<T>).data;
}

/** 语法糖，让 endpoints 里的调用短一些 */
export const http = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('GET', path, options),
  post: <T>(path: string, options?: RequestOptions) =>
    request<T>('POST', path, options),
  patch: <T>(path: string, options?: RequestOptions) =>
    request<T>('PATCH', path, options),
  put: <T>(path: string, options?: RequestOptions) =>
    request<T>('PUT', path, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, options),
};

/** 生成幂等键，写操作用 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
